import React, { useRef } from 'react';
import {
  AcademicCapIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  BookmarkIcon,
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  HandThumbDownIcon,
  HandThumbUpIcon,
  LinkIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  useInView,
  usePlayback,
  usePrefersReducedMotion,
  useSmoothCoworkScroll,
} from './coworkChatHooks';
import { getCoworkScenario } from './coworkChatScenarios';

function CardChrome({ title, compact }) {
  return (
    <div className={`flex items-start justify-between gap-1.5 border-b border-ink-100/90 ${compact ? 'px-1.5 py-1' : 'px-2 py-1.5'}`}>
      <p className={`font-semibold leading-snug text-ink-800 ${compact ? 'text-[7px]' : 'text-[9px] sm:text-[10px]'}`}>
        {title}
      </p>
      <div className="flex shrink-0 items-center gap-0.5 text-ink-400">
        <BookmarkIcon className={compact ? 'h-2 w-2' : 'h-3 w-3'} aria-hidden="true" />
        {!compact && (
          <>
            <LinkIcon className="h-3 w-3" aria-hidden="true" />
            <EllipsisHorizontalIcon className="h-3 w-3" aria-hidden="true" />
            <span className="inline-flex items-center gap-0.5 rounded border border-ink-200/90 px-1 py-px text-[8px] font-medium text-ink-600">
              Explore
              <ArrowTopRightOnSquareIcon className="h-2 w-2" aria-hidden="true" />
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function ChartLegend({ legend, min, max, compact }) {
  return (
    <div
      className={`mb-1 flex items-center gap-1 px-0.5 font-semibold uppercase tracking-wide text-ink-500 ${compact ? 'text-[7px]' : 'text-[7px] sm:text-[8px]'}`}
    >
      <span className="shrink-0">{legend}</span>
      <div className="h-0.5 min-w-0 flex-1 rounded-full bg-gradient-to-r from-amber-200 via-orange-400 to-red-600" />
      <span className="tabular-nums">{min}</span>
      <span className="tabular-nums">{max}</span>
    </div>
  );
}

function CoWorkComposer({ compact }) {
  return (
    <div className={`shrink-0 border-t border-ink-100/80 bg-white ${compact ? 'px-1.5 py-1' : 'px-2.5 py-2'}`}>
      <div className={`rounded-xl border border-ink-200/80 ${compact ? 'px-1.5 py-1' : 'px-2.5 py-2'}`}>
        <p className={`text-ink-400 ${compact ? 'text-[8px]' : 'text-[11px]'}`}>What would you like to know?</p>
        <div className={`flex items-center justify-between gap-1 ${compact ? 'mt-1' : 'mt-2'}`}>
          <div className="flex min-w-0 items-center gap-1">
            <span
              className={`flex shrink-0 items-center justify-center rounded-full border border-ink-200/90 text-ink-500 ${compact ? 'h-4 w-4' : 'h-6 w-6'}`}
            >
              <PlusIcon className={compact ? 'h-2 w-2' : 'h-3 w-3'} aria-hidden="true" />
            </span>
            <span
              className={`truncate rounded-full border border-ink-200/90 font-medium text-ink-600 ${compact ? 'px-1 py-px text-[7px]' : 'px-2 py-0.5 text-[10px]'}`}
            >
              SemanticHR Copilot Analyst
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!compact && (
              <span className="hidden items-center gap-0.5 rounded-full border border-ink-200/90 px-2 py-0.5 text-[10px] text-ink-600 sm:inline-flex">
                <AcademicCapIcon className="h-3 w-3" aria-hidden="true" />
                Extended thinking
              </span>
            )}
            <span
              className={`flex items-center justify-center rounded-full bg-ink-900 ${compact ? 'h-4 w-4' : 'h-6 w-6'}`}
            >
              <span className={`rounded-[1px] bg-white ${compact ? 'h-1 w-1' : 'h-2 w-2'}`} aria-hidden="true" />
            </span>
          </div>
        </div>
      </div>
      {!compact && (
        <p className="mt-1 text-center text-[9px] text-ink-400">Agents can make mistakes, double-check responses.</p>
      )}
    </div>
  );
}

function StepBlock({ show, children }) {
  if (!show) return null;
  return <div className="ia-cowork-block">{children}</div>;
}

function DataTable({ table, compact, maxRows }) {
  const rows = maxRows ? table.rows.slice(0, maxRows) : table.rows;
  return (
    <div className="overflow-hidden rounded-lg border border-ink-200/80 bg-white">
      <CardChrome title={table.title} compact={compact} />
      <table className={`w-full text-left ${compact ? 'text-[7px]' : 'text-[8px] sm:text-[9px]'}`}>
        <thead>
          <tr
            className={`border-b border-ink-100 bg-ink-50/50 font-semibold uppercase tracking-wide text-ink-500 ${compact ? 'text-[7px]' : 'text-[7px] sm:text-[8px]'}`}
          >
            {table.columns.map((col) => (
              <th key={col} className={`${compact ? 'px-1 py-0.5' : 'px-1.5 py-1'} ${col !== table.columns[0] ? 'text-right' : ''}`}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} className="border-b border-ink-50 text-ink-700">
              {row.map((cell, ci) => (
                <td
                  key={`${row[0]}-${ci}`}
                  className={`${compact ? 'px-1 py-px' : 'px-1.5 py-0.5'} ${ci > 0 ? 'text-right tabular-nums' : 'font-medium'} ${ci === row.length - 1 && cell.includes('%') ? 'font-semibold' : ''}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {table.footer && (
        <p className={`py-0.5 text-center font-medium text-brand-600 ${compact ? 'text-[7px]' : 'text-[9px]'}`}>
          {table.footer}
        </p>
      )}
    </div>
  );
}

function BarChart({ chart, compact, maxRows }) {
  const rows = maxRows ? chart.rows.slice(0, maxRows) : chart.rows;
  const maxVal = chart.max ?? rows[0]?.value ?? 1;

  return (
    <div className="overflow-hidden rounded-lg border border-ink-200/80 bg-white">
      <CardChrome title={chart.title} compact={compact} />
      <div className={compact ? 'px-1 py-1' : 'px-2 py-1.5'}>
        <ChartLegend legend={chart.legend} min={chart.min} max={chart.max} compact={compact} />
        <div className="space-y-0.5">
          {rows.map((row) => {
            const pct = (row.value / maxVal) * 100;
            const tone =
              pct > 85 ? 'from-red-500 to-red-600' : pct > 65 ? 'from-orange-400 to-red-500' : 'from-amber-300 to-orange-400';
            return (
              <div key={row.label} className="grid grid-cols-[1fr_1.35fr] items-center gap-0.5">
                <span className={`truncate text-ink-700 ${compact ? 'text-[7px]' : 'text-[8px] sm:text-[9px]'}`}>
                  {row.label}
                </span>
                <div className={`rounded-sm bg-ink-100/80 ${compact ? 'h-1' : 'h-2'}`}>
                  <div
                    className={`ia-cowork-bar h-full rounded-sm bg-gradient-to-r ${tone}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InsightsBlock({ insights, compact, listLabel = 'Key takeaways:' }) {
  if (!insights) return null;
  const textSize = compact ? 'text-[7px]' : 'text-[10px] sm:text-[11px]';

  return (
    <div className={`leading-relaxed text-ink-700 ${textSize}`}>
      {insights.title && <p className="font-semibold text-ink-900">{insights.title}</p>}
      {insights.sections?.map((section) => (
        <div key={section.heading || section.bullets?.[0]} className={insights.title || section.heading ? 'mt-1' : ''}>
          {section.heading && <p className="font-semibold text-ink-900">{section.heading}</p>}
          <ul className="mt-0.5 list-disc space-y-0.5 pl-3 marker:text-ink-400">
            {section.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TakeawaysBlock({ takeaways, compact }) {
  const textSize = compact ? 'text-[7px]' : 'text-[10px] sm:text-[11px]';
  return (
    <div className={`leading-relaxed text-ink-700 ${textSize}`}>
      <p className="font-semibold text-ink-900">Key takeaways:</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-3 marker:text-ink-400">
        {takeaways.map((item) => (
          <li key={item.lead}>
            <span className="font-semibold text-ink-900">{item.lead}</span> {item.body}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ObservationsBlock({ observations, compact }) {
  const textSize = compact ? 'text-[7px]' : 'text-[10px] sm:text-[11px]';
  return (
    <div className={`leading-relaxed text-ink-700 ${textSize}`}>
      <p className="font-semibold text-ink-900">Key observations:</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-3 marker:text-ink-400">
        {observations.map((item) => (
          <li key={item.lead}>
            <span className="font-semibold text-ink-900">{item.lead}</span> {item.body}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TierTables({ tierTables, compact, maxRows = 2 }) {
  return (
    <div className="space-y-1.5">
      {tierTables.map((tier) => (
        <div key={tier.tier} className="overflow-hidden rounded-lg border border-ink-200/80 bg-white">
          <div
            className={`border-b border-ink-100/90 px-1.5 py-0.5 font-semibold text-ink-800 ${compact ? 'text-[7px]' : 'text-[9px]'}`}
          >
            <span className={tier.tone === 'red' ? 'text-red-600' : 'text-amber-600'} aria-hidden="true">
              ●{' '}
            </span>
            {tier.tier}
          </div>
          <table className={`w-full text-left ${compact ? 'text-[7px]' : 'text-[8px]'}`}>
            <thead>
              <tr className="border-b border-ink-100 bg-ink-50/50 text-[7px] font-semibold uppercase text-ink-500">
                {tier.columns.map((col) => (
                  <th key={col} className="px-1 py-0.5">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tier.rows.slice(0, maxRows).map((row) => (
                <tr key={row[0]} className="border-b border-ink-50 text-ink-700">
                  <td className="px-1 py-0.5 font-medium">{row[0]}</td>
                  <td className="px-1 py-0.5">{row[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function FollowUpsBlock({ followUps, compact }) {
  return (
    <div className={`space-y-1 border-t border-ink-100/80 pt-1.5 ${compact ? '' : 'space-y-1.5 pt-2'}`}>
      <div className="flex items-center gap-1.5 text-ink-400">
        <DocumentDuplicateIcon className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} aria-hidden="true" />
        <HandThumbUpIcon className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} aria-hidden="true" />
        {!compact && (
          <>
            <HandThumbDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            <EllipsisHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </>
        )}
      </div>
      {followUps.slice(0, compact ? 1 : undefined).map((prompt) => (
        <div
          key={prompt}
          className={`flex items-start gap-1 leading-snug text-ink-600 ${compact ? 'text-[7px]' : 'text-[10px]'}`}
        >
          <ArrowUturnLeftIcon className={`mt-0.5 shrink-0 text-ink-400 ${compact ? 'h-2 w-2' : 'h-3 w-3'}`} aria-hidden="true" />
          <span>{prompt}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Animated CoWork chat mock: fixed height panel with hidden scrollbar and eased auto-scroll.
 */
export default function CoWorkChatVisual({
  scenarioId = 'cowork_clip2',
  compact = false,
  showComposer = true,
  playbackOffsetMs = 0,
  loop = true,
  className = '',
}) {
  const scenario = getCoworkScenario(scenarioId);
  const reducedMotion = usePrefersReducedMotion();
  const { ref, inView } = useInView(compact ? 0.2 : 0.15);
  const active = reducedMotion || inView;
  const scrollRef = useRef(null);

  const playbackConfig = scenario
    ? {
        steps: scenario.steps,
        stepDelays: scenario.stepDelays,
        loopPause: scenario.loopPause,
        offsetMs: playbackOffsetMs,
      }
    : { steps: [], stepDelays: [], loopPause: 0, offsetMs: 0 };

  const { isVisible, visibleCount, cycle, resetting } = usePlayback(active, playbackConfig, reducedMotion, loop);

  useSmoothCoworkScroll(scrollRef, visibleCount, reducedMotion, { cycle, resetting });

  if (!scenario) return null;

  const showQuerying = isVisible('querying') && !isVisible('retrieved');
  const textSize = compact ? 'text-[8px] leading-snug' : 'text-[11px] leading-snug';
  const introSize = compact ? 'text-[8px] leading-relaxed' : 'text-[11px] leading-relaxed';
  const maxTableRows = compact ? 4 : undefined;
  const maxChartRows = compact ? 4 : undefined;

  const showTable = (isVisible('content') || isVisible('table')) && scenario.table;
  const showTiers = isVisible('content') && scenario.tierTables;
  const showTakeaways = isVisible('takeaways') && scenario.takeaways;
  const showInsights = isVisible('insights') && scenario.insights;
  const showObservations = isVisible('insights') && scenario.observations;

  return (
    <div
      ref={ref}
      className={`ia-cowork-demo h-full max-h-full w-full overflow-hidden ${compact ? 'ia-cowork-demo--compact' : ''} ${className}`}
      role="img"
      aria-label={`Mock-up of MyOrgLab CoWork answering "${scenario.question}" with a data table, chart and key takeaways`}
    >
      <div aria-hidden="true" className="contents">
      <div
        className={`flex h-full max-h-full flex-col overflow-hidden bg-white ${
          showComposer ? 'rounded-2xl border border-ink-200/80 shadow-sm' : 'rounded-none border-0 shadow-none'
        }`}
      >
        <div
          ref={scrollRef}
          className={`ia-cowork-scroll min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain ${
            showComposer ? 'px-2 py-2 sm:px-2.5 sm:py-2.5' : 'px-1.5 py-1.5 sm:px-2 sm:py-2'
          }`}
        >
          <div key={cycle} className={compact ? 'space-y-1' : 'space-y-2'}>
            <StepBlock show={isVisible('question')}>
              <div className="flex justify-end">
                <div
                  className={`max-w-[94%] rounded-xl rounded-tr-sm bg-ink-100/70 text-ink-800 ${compact ? 'px-1.5 py-1' : 'px-2.5 py-2'} ${textSize}`}
                >
                  {scenario.question}
                </div>
              </div>
            </StepBlock>

            {showQuerying ? (
              <div className={`ia-cowork-block flex items-center gap-1 text-ink-500 ${textSize}`}>
                <SparklesIcon className={`shrink-0 text-brand-500 ${compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} aria-hidden="true" />
                <span>
                  Querying data… <span className="text-ink-400">&gt;</span>
                </span>
              </div>
            ) : null}

            <StepBlock show={isVisible('retrieved')}>
              <div className={`flex items-center gap-1 text-ink-500 ${textSize}`}>
                <SparklesIcon className={`shrink-0 text-brand-500 ${compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} aria-hidden="true" />
                <span>
                  Retrieved data and created chart <span className="text-ink-400">&gt;</span>
                </span>
              </div>
            </StepBlock>

            <StepBlock show={isVisible('intro')}>
              <p className={`text-ink-700 ${introSize}`}>{scenario.intro}</p>
            </StepBlock>

            <StepBlock show={showTable}>
              <DataTable table={scenario.table} compact={compact} maxRows={maxTableRows} />
            </StepBlock>

            <StepBlock show={showTiers}>
              <TierTables tierTables={scenario.tierTables} compact={compact} maxRows={compact ? 2 : 3} />
            </StepBlock>

            <StepBlock show={isVisible('chart') && scenario.chart}>
              <BarChart chart={scenario.chart} compact={compact} maxRows={maxChartRows} />
            </StepBlock>

            <StepBlock show={showObservations}>
              <ObservationsBlock observations={scenario.observations} compact={compact} />
            </StepBlock>

            <StepBlock show={showTakeaways}>
              <TakeawaysBlock takeaways={scenario.takeaways} compact={compact} />
            </StepBlock>

            <StepBlock show={showInsights}>
              <InsightsBlock insights={scenario.insights} compact={compact} />
            </StepBlock>

            <StepBlock show={isVisible('summary') && scenario.summary}>
              <p className={`text-ink-700 ${introSize}`}>{scenario.summary}</p>
            </StepBlock>

            <StepBlock show={isVisible('followups') && scenario.followUps}>
              <FollowUpsBlock followUps={scenario.followUps} compact={compact} />
            </StepBlock>
          </div>
        </div>

        {showComposer ? <CoWorkComposer compact={compact} /> : null}
      </div>
      </div>
    </div>
  );
}
